/**
 * Engine routing rules — the ONE place that says which STT engine may touch which language.
 *
 * Why: AssemblyAI forced to 'af' mistranscribes Afrikaans as Dutch (production defect,
 * 4 Oct 2026, paying customer), Deepgram has no 'af' model, and AssemblyAI/Deepgram/Groq
 * produce garbage for the SA-indigenous languages. Gemini (and Lelapa/ElevenLabs for
 * SA-indigenous) are the only engines allowed for these languages.
 *
 * Language list mirrors the repo's existing guards:
 *   - src/config/languages.ts          'af' (recommended tier) + zu/xh/st/tn/ve ('sa-indigenous' tier)
 *   - src/hooks/useTranscription.ts    GEMINI_ONLY_LANGS = zu, xh, st, tn, ve
 *   - api/transcribe.ts                "NEVER fall back to Deepgram/Groq" (Lelapa languages)
 * nso/ss/ts/nr are NOT in src/config/languages.ts (only a Gemini locale map in
 * lib/gemini-transcribe.ts knows nso/ts/ss), so they are not offered by the product; they are
 * listed here defensively so enabling them later cannot silently open a bad route.
 *
 * Pure module: no imports, safe for both api/ (Vercel) and src/ (browser).
 */

export type SttEngine = 'assemblyai' | 'deepgram' | 'groq' | 'gemini' | 'lelapa';

const NON_GEMINI_ENGINES: string[] = ['assemblyai', 'deepgram', 'groq'];

export const NEVER_ENGINES: Record<string, string[]> = {
  af: [...NON_GEMINI_ENGINES],
  zu: [...NON_GEMINI_ENGINES],
  xh: [...NON_GEMINI_ENGINES],
  st: [...NON_GEMINI_ENGINES],
  tn: [...NON_GEMINI_ENGINES],
  ve: [...NON_GEMINI_ENGINES],
  nso: [...NON_GEMINI_ENGINES],
  ss: [...NON_GEMINI_ENGINES],
  ts: [...NON_GEMINI_ENGINES],
  nr: [...NON_GEMINI_ENGINES],
};

export const ENGINE_NOT_ALLOWED_CODE = 'ENGINE_NOT_ALLOWED_FOR_LANGUAGE';

/**
 * Reduce any caller-supplied language to the base subtag the rules are keyed on:
 * strings only, trimmed, lowercased, 'af-ZA' / 'af_za' -> 'af'. Anything else
 * (number, null, object, empty) is undefined. Never throws.
 */
export function normaliseLanguage(lang: unknown): string | undefined {
  if (typeof lang !== 'string') return undefined;
  const base = lang.trim().toLowerCase().split(/[-_]/)[0];
  return base || undefined;
}

/** Undefined / 'auto' / unknown languages are unrestricted (auto-detect is handled separately). */
export function isEngineAllowed(lang: unknown, engine: SttEngine): boolean {
  const base = normaliseLanguage(lang);
  if (!base) return true;
  const banned = Object.prototype.hasOwnProperty.call(NEVER_ENGINES, base) ? NEVER_ENGINES[base] : undefined;
  return !banned || !banned.includes(engine);
}

/**
 * Path-4 guard. With no language chosen, AssemblyAI/Deepgram/Groq auto-detection resolves
 * Afrikaans to Dutch, so a detected nl/af from those engines cannot be trusted either way.
 * Gemini detecting 'af' is correct and accepted. A chosen language is always accepted.
 */
export function isUndetectableDutchAfrikaans(
  chosenLanguage: string | undefined,
  detectedLanguage: unknown,
  engine: unknown,
): boolean {
  if (chosenLanguage !== undefined) return false;
  const detected = normaliseLanguage(detectedLanguage);
  if (detected !== 'nl' && detected !== 'af') return false;
  const eng = typeof engine === 'string' ? engine.toLowerCase() : '';
  return NON_GEMINI_ENGINES.some(e => eng.includes(e));
}

/** Shown by History 'Complete' when an auto-detected nl/af result is refused (same wording as the pipeline guard in useTranscription). */
export const DUTCH_AFRIKAANS_NOT_CHARGED_MESSAGE =
  'We detected Dutch/Afrikaans. Please choose Afrikaans or Dutch in the language picker and run again — you have not been charged.';

export function assertEngineAllowed(lang: string | undefined, engine: SttEngine): void {
  if (!isEngineAllowed(lang, engine)) {
    throw new Error(`${ENGINE_NOT_ALLOWED_CODE}: engine '${engine}' must not be used for language '${lang}'`);
  }
}

/** Truthful user-facing message when a restricted language has no permitted engine available. */
export function engineUnavailableMessage(lang: string | undefined, label?: string): string {
  const name = label ?? (lang === 'af' ? 'Afrikaans' : (lang ?? 'This language').toUpperCase());
  return `${name} transcription is temporarily unavailable. You have not been charged. Please try again in a few minutes.`;
}
